import { useEffect, useMemo, useState } from 'react'
import { Button, Form, Input, Modal, Space } from 'antd'
import { initPlatformEdit, manualRefreshPlatform, platformTypeToId, updatePlatform } from '@/features/platform/api'
import type { PlatformCreatePayload, PlatformType } from '@/features/platform/types'
import { useToast } from '@/contexts/ToastContext'

type Props = {
  open: boolean
  platformId: string
  clientId: string
  companyName?: string
  companyShortName?: string
  platformType: Extract<PlatformType, 'FYI' | 'Karbon'>
  onCancel: () => void
  onSuccess: () => void
}

function buildPlatformName(platformType: Extract<PlatformType, 'FYI' | 'Karbon'>, companyName?: string, companyShortName?: string) {
  const baseName = companyShortName?.trim() || companyName?.trim() || 'Client'
  return `${baseName} ${platformType}`
}

export default function ManualPlatformConnectModal({
  open,
  platformId,
  clientId,
  companyName,
  companyShortName,
  platformType,
  onCancel,
  onSuccess,
}: Props) {
  const { addToast } = useToast()
  const [form] = Form.useForm<PlatformCreatePayload>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isInitializing, setIsInitializing] = useState(false)
  const isFyi = platformType === 'FYI'

  const modalTitle = useMemo(() => `Connect Your Data`, [])

  useEffect(() => {
    if (!open || !platformId) return

    let cancelled = false
    const loadPlatform = async () => {
      setIsInitializing(true)
      try {
        const platform = await initPlatformEdit(platformId)
        if (cancelled || !platform) return
        form.setFieldsValue({
          apiKey: platform.apiKey,
          apiSecret: platform.apiSecret,
          username: platform.username,
          password: platform.password,
          account: platform.account,
          warehouse: platform.warehouse,
        })
      } catch (error) {
        if (!cancelled) {
          addToast(error instanceof Error ? error.message : 'Unable to load platform details', 'error')
        }
      } finally {
        if (!cancelled) setIsInitializing(false)
      }
    }

    void loadPlatform()
    return () => {
      cancelled = true
    }
  }, [addToast, form, open, platformId])

  const handleCancel = () => {
    if (isSubmitting || isInitializing) return
    form.resetFields()
    onCancel()
  }

  const handleSubmit = async () => {
    if (!platformId) {
      addToast('Unable to find the signup platform to connect', 'error')
      return
    }
    const values = await form.validateFields()
    setIsSubmitting(true)
    try {
      const existingPlatform = await initPlatformEdit(platformId)
      await updatePlatform({
        id: platformId,
        clientId,
        name: existingPlatform?.name || buildPlatformName(platformType, companyName, companyShortName),
        type: platformType,
        status: existingPlatform?.status || 'Pending',
        apiKey: values.apiKey?.trim(),
        apiSecret: values.apiSecret?.trim(),
        username: values.username?.trim(),
        password: values.password,
        account: values.account?.trim(),
        warehouse: values.warehouse?.trim(),
      })
      await manualRefreshPlatform({
        platformId,
        typeId: platformTypeToId(platformType),
      })
      form.resetFields()
      onSuccess()
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Unable to connect platform', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      title={modalTitle}
      onCancel={handleCancel}
      footer={
        <Space>
          <Button type="primary" loading={isSubmitting} disabled={isInitializing} onClick={() => void handleSubmit()}>
            Save
          </Button>
          <Button onClick={handleCancel} disabled={isSubmitting || isInitializing}>
            Cancel
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" disabled={isInitializing}>
        {isFyi ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Form.Item name="apiKey" label="ApiKey*" rules={[{ required: true, message: 'API Key is required' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="apiSecret" label="ApiSecret*" rules={[{ required: true, message: 'API Secret is required' }]}>
              <Input />
            </Form.Item>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Form.Item name="username" label="Username*" rules={[{ required: true, message: 'Username is required' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="password" label="Password*" rules={[{ required: true, message: 'Password is required' }]}>
              <Input.Password />
            </Form.Item>
            <Form.Item name="account" label="Account Name*" rules={[{ required: true, message: 'Account Name is required' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="warehouse" label="Warehouse*" rules={[{ required: true, message: 'Warehouse is required' }]}>
              <Input />
            </Form.Item>
          </div>
        )}
      </Form>
    </Modal>
  )
}
